import { cloneElement, isValidElement, type ReactNode } from 'react';
import { useTranslation } from '@doccareplus/shared/lib';
import { hasClinicCapability, useClinicAccessQuery } from '@features/clinic-access';
import type { UICommand } from '@features/global-search';
import { navSections, quickActions } from './navModel';

/** Resize a nav icon to fit the search result badge (nav icons ship at the default 24px). */
function sizedIcon(icon: ReactNode): ReactNode {
  return isValidElement<{ className?: string }>(icon)
    ? cloneElement(icon, { className: 'h-[17px] w-[17px]' })
    : icon;
}

/**
 * Navigation page commands for global search — every sidebar destination the current role/plan can
 * reach, flattened and capability-filtered. Shared by the desktop palette and mobile sheet so both
 * offer the same "jump to page" list. Quick-action commands are built by each shell instead, since
 * only the shell knows how to open its own drawers/sheets.
 */
export function useNavigationPageCommands(): UICommand[] {
  const { t } = useTranslation('common');
  const accessQuery = useClinicAccessQuery();

  return navSections.flatMap((section) =>
    section.routes
      .filter((route) => !route.permissions || hasClinicCapability(accessQuery.data, route.permissions, route.feature))
      .map<UICommand>((route) => ({
        id: `page:${route.path}`,
        category: 'pages',
        title: t(route.labelKey),
        icon: sizedIcon(route.icon),
        to: route.path,
      })),
  );
}

/** The quick actions the current role/plan can perform (same filter the shells use to show the "New" menu). */
export function useVisibleQuickActions() {
  const accessQuery = useClinicAccessQuery();
  return quickActions.filter(
    (action) => !action.permissions || hasClinicCapability(accessQuery.data, action.permissions, action.feature),
  );
}
